import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    // After replacing, update media grouping on the new gallery
    const replacedGallery = document.querySelector('media-gallery');
    if (replacedGallery && replacedGallery.updateMediaGrouping) {
      replacedGallery.updateMediaGrouping();
    }
  };

  /**
   * Updates the visibility of media items based on variant grouping.
   * Hides media that don't match the currently selected variant's grouping.
   */
  updateMediaGrouping() {
    const groupVariants = this.getAttribute('data-group-variants');
    const hasOnlyDefaultVariant = this.getAttribute('data-has-only-default-variant');
    const disableImageGrouping = this.getAttribute('data-disable-image-grouping');

    if (groupVariants === 'false' || hasOnlyDefaultVariant === 'true' || disableImageGrouping === 'true') {
      return;
    }

    // Find the active/featured media item
    const mediaItems = this.querySelectorAll('slideshow-slide, li[ref="media[]"]');
    const featuredMedia = Array.from(mediaItems).find(media =>
      media.classList.contains('is-active') ||
      media.getAttribute('aria-hidden') === 'false'
    ) || mediaItems[0];

    const featuredVariantGrouping = featuredMedia ? featuredMedia.getAttribute('data-variant-grouping') : null;

    // Update visibility for all media items (carousel slides and grid items)
    mediaItems.forEach(mediaItem => {
      const mediaVariantGrouping = mediaItem.getAttribute('data-variant-grouping');
      if (mediaVariantGrouping === featuredVariantGrouping) {
        mediaItem.classList.remove('hide-image');
      } else {
        mediaItem.classList.add('hide-image');
      }
    });

    // Update visibility for zoom dialog items
    const zoomDialog = this.querySelector('zoom-dialog');
    if (zoomDialog) {
      const zoomMediaItems = zoomDialog.querySelectorAll('.dialog-zoomed-gallery li');
      zoomMediaItems.forEach(mediaItem => {
        const mediaVariantGrouping = mediaItem.getAttribute('data-variant-grouping');
        if (mediaVariantGrouping === featuredVariantGrouping) {
          mediaItem.classList.remove('hide-image');
        } else {
          mediaItem.classList.add('hide-image');
        }
      });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
